import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/api';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 48px);
  background: ${({ theme }) => theme.colors.canvas};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    border: 0.5rem solid #fff;
    padding: 1rem;
  }
`;

const Title = styled.h1`
  margin: 1rem 0;
  font-size: 2rem;
  text-align: left;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.display};
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Divider = styled.hr`
  margin: 0 1rem 1rem;
  border: none;
  border-top: 2px solid #e2e8f0;

  @media (max-width: 768px) {
    margin: 0 0.5rem 1rem;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: table;
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: auto;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TableRow = styled.div`
  display: table-row;
  width: 100%;
`;

const TableHeader = styled(TableRow)`
  font-weight: bold;
  background-color: ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TableCell = styled.div`
  display: table-cell;
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: pre-wrap;
  width: ${({ width }) => width || 'auto'};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Loading = styled.div`
  text-align: center;
  margin-top: 2rem;
  font-size: 1.25rem;
  color: #4a5568;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-top: 1rem;
  }
`;

const Error = styled.div`
  text-align: center;
  margin-top: 2rem;
  font-size: 1.25rem;
  color: #e53e3e;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-top: 1rem;
  }
`;

const BrotherSubmissions = () => {
  const { fratId, eventId } = useParams();
  const [eventDetails, setEventDetails] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventAndSubmissions = async () => {
      try {
        // First fetch event details
        const eventResponse = await api.get(`/events/${eventId}?fraternity=${fratId}`);
        const eventData = eventResponse.data.data;
        setEventDetails(eventData);

        // Then fetch submissions
        const submissionsResponse = await api.get(
          `/events/${eventId}/submissions?fraternity=${fratId}&type=brother`
        );
        setSubmissions(submissionsResponse.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.error || 'Failed to load event details or submissions');
        setLoading(false);
      }
    };

    if (fratId && eventId) {
      fetchEventAndSubmissions();
    }
  }, [fratId, eventId]);

  if (loading) return <Loading>Loading...</Loading>;
  if (error) return <Error>{error}</Error>;
  if (!eventDetails) return <Error>No event details found</Error>;

  return (
    <PageContainer>
      <Title>{eventDetails.name} - Brother Submissions</Title>
      <Divider />
      {submissions.length === 0 && <Loading>No brother submissions yet.</Loading>}
      <TableContainer>
        <TableHeader>
          <TableCell width="15%">Brother Name</TableCell>
          {eventDetails.brotherForm.questions.map((q, index) => (
            <TableCell key={index} width={q.questionType === 'textarea' ? '30%' : '15%'}>
              {q.question}
            </TableCell>
          ))}
        </TableHeader>

        {submissions.map((submission, index) => {
          const parsedResponses = JSON.parse(submission.responses || '{}');
          return (
            <TableRow key={index}>
              <TableCell width="15%">{submission.brother?.name || 'Unknown Brother'}</TableCell>
              {eventDetails.brotherForm.questions.map((q, qIndex) => (
                <TableCell key={qIndex} width={q.questionType === 'textarea' ? '30%' : '15%'}>
                  {Array.isArray(parsedResponses[qIndex])
                    ? parsedResponses[qIndex].join(', ')
                    : parsedResponses[qIndex] || 'No Response'}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableContainer>
    </PageContainer>
  );
};

export default BrotherSubmissions;